import { CommitmentEvent, HistoryEvent } from '../models/impact-panel.model';

export const ZERO_ADDRESS = '0x0000000000000000000000000000000000000000';

export const PCA_LABELS = ['Personas', 'Empresas', 'Aplicaciones'];

export const CATEGORY_LABEL: Record<string, string> = {
  AYUDA: 'Ayuda',
  MEJORA: 'Mejora',
  INTERCAMBIO: 'Intercambio',
  VALIDACION: 'Validación',
  DENUNCIA: 'Denuncia',
  '': 'Sin categoría',
};

export type ActivityIconKind =
  | 'ayuda'
  | 'validacion'
  | 'denuncia'
  | 'mejora'
  | 'intercambio'
  | 'collaboration'
  | 'doc-added'
  | 'doc-removed'
  | 'doc-updated'
  | 'commitment-added'
  | 'commitment-removed'
  | 'commitment-updated'
  | 'comment'
  | 'finalized'
  | 'status'
  | 'deleted'
  | 'default';

export const ACTIVITY_ICON_LABEL: Record<ActivityIconKind, string> = {
  ayuda: 'Ayuda',
  validacion: 'Validación',
  denuncia: 'Denuncia',
  mejora: 'Mejora',
  intercambio: 'Intercambio',
  collaboration: 'Colaboración',
  'doc-added': 'Documento subido',
  'doc-removed': 'Documento eliminado',
  'doc-updated': 'Documentos actualizados',
  'commitment-added': 'Adhesión al compromiso',
  'commitment-removed': 'Baja del compromiso',
  'commitment-updated': 'Compromiso actualizado',
  comment: 'Comentario',
  finalized: 'Colaboración finalizada',
  status: 'Estado actualizado',
  deleted: 'Eliminado',
  default: 'Evento',
};

export function categoryIconKind(category?: string): ActivityIconKind {
  const key = (category || '').toUpperCase();
  if (key === 'AYUDA') return 'ayuda';
  if (key === 'VALIDACION') return 'validacion';
  if (key === 'DENUNCIA') return 'denuncia';
  if (key === 'MEJORA') return 'mejora';
  if (key === 'INTERCAMBIO') return 'intercambio';
  return 'collaboration';
}

export function eventIconKind(event: { type: string; subType?: string; data?: any }): ActivityIconKind {
  const type = String(event.type || '').toLowerCase();
  const sub = String(event.subType || '').toLowerCase();

  if (type.includes('document')) {
    const counts = documentChangeCounts(event.data);
    if (counts.added && !counts.removed) return 'doc-added';
    if (counts.removed && !counts.added) return 'doc-removed';
    return 'doc-updated';
  }

  if (type.includes('comment')) return 'comment';

  if (type.includes('collaboration')) {
    if (type.includes('deleted') || type.includes('removed')) return 'deleted';
    if (type.includes('created') || type.includes('added')) return categoryIconKind(event.data?.category);
    if (sub === 'finalized') return 'finalized';
    return 'status';
  }

  if (type.includes('commitment')) {
    if (sub === 'disabled' || type.includes('deleted') || type.includes('removed')) {
      return 'commitment-removed';
    }
    if (sub === 'enabled' || type.includes('created') || type.includes('added')) {
      return 'commitment-added';
    }
    return 'commitment-updated';
  }

  if (type.includes('deleted') || type.includes('removed')) return 'deleted';
  return 'default';
}

export const ASSIGNED_STATUS_LABEL: Record<string, { text: string; cssClass: string }> = {
  complies: { text: 'Cumple', cssClass: 'suc' },
  has_intention: { text: 'Tiene intención', cssClass: 'warn' },
  not_adhered: { text: 'No adhiere', cssClass: 'def' },
};

const DETAIL_EVENT_LABELS: Record<string, { label: string; cssClass: string }> = {
  PersonCommitmentAdded: { label: 'Se adhirió al compromiso', cssClass: 'created' },
  PersonCommitmentRemoved: { label: 'Se dio de baja del compromiso', cssClass: 'deleted' },
  PersonCommitmentModified: { label: 'Se actualizó el compromiso', cssClass: 'update' },
  PersonCommitmentDocumentsUpdated: {
    label: 'Se actualizaron los documentos',
    cssClass: 'update',
  },
};

export function shortAddr(address?: string | null): string {
  return address ? `${address.slice(0, 6)}…${address.slice(-4)}` : '—';
}

export function shortTx(tx?: string | null): string {
  return tx ? `${tx.slice(0, 6)}…${tx.slice(-4)}` : '—';
}

const MESES = [
  'ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic',
];

// Un ISO sin zona ("2026-10-16T14:30:00") se interpretaría como hora local del navegador y
// quedaría corrida; el backend emite UTC, así que se fuerza la `Z` y se muestra en hora local.
// Una fecha sin hora ("2026-10-16") es un día de calendario, no un instante: `new Date` la
// tomaría como medianoche UTC y en Uruguay (UTC-3) mostraría el día anterior, así que se
// construye en hora local.
function parseUtc(iso: string): Date {
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (dateOnly) return new Date(+dateOnly[1], +dateOnly[2] - 1, +dateOnly[3]);
  const hasTime = iso.includes('T');
  const hasZone = /(Z|[+-]\d{2}:?\d{2})$/i.test(iso);
  return new Date(hasTime && !hasZone ? `${iso}Z` : iso);
}

export function fmtDate(iso?: string | null): string {
  if (!iso) return '—';
  const date = parseUtc(iso);
  if (isNaN(date.getTime())) return '—';
  const day = String(date.getDate()).padStart(2, '0');
  return `${day} ${MESES[date.getMonth()]} ${date.getFullYear()}`;
}

export function fmtTime(iso?: string | null): string {
  if (!iso) return '—';
  const date = parseUtc(iso);
  if (isNaN(date.getTime())) return '—';
  const h = String(date.getHours()).padStart(2, '0');
  const m = String(date.getMinutes()).padStart(2, '0');
  return `${h}:${m}`;
}

export function fmtDateTime(iso?: string | null): string {
  if (!iso) return '—';
  return `${fmtDate(iso)} · ${fmtTime(iso)}`;
}

export function isSelf(address: string | undefined, walletAddress: string): boolean {
  return !!address && !!walletAddress && address.toLowerCase() === walletAddress.toLowerCase();
}

export function isZeroAddress(address?: string | null): boolean {
  return !!address && address.toLowerCase() === ZERO_ADDRESS;
}

export function assignedStatusMeta(status?: string): { text: string; cssClass: string } {
  return ASSIGNED_STATUS_LABEL[status || ''] || { text: status || 'Sin estado', cssClass: 'def' };
}

export function collaborationStatusMeta(data: any): { text: string; cssClass: string } {
  const status = String(data?.status ?? data?.collaborationStatus ?? '').toLowerCase();
  const code = data?.statusCode ?? data?.collaborationStatusCode;
  const srcSat = data?.sourceSatisfaction;
  const tgtSat = data?.targetSatisfaction;
  if (status === 'finalized' || code === 3) {
    if (srcSat === 'not_satisfied' || tgtSat === 'not_satisfied') {
      return { text: 'Finalizada insatisfactoriamente', cssClass: 'err' };
    }
    if (srcSat === 'satisfied' || tgtSat === 'satisfied') {
      return { text: 'Finalizada satisfactoriamente', cssClass: 'suc' };
    }
    return { text: 'Finalizada', cssClass: 'suc' };
  }
  if (status === 'reported' || code === 4) return { text: 'Reportada', cssClass: 'err' };
  if (status === 'active' || code === 2) return { text: 'Activa', cssClass: 'info' };
  if (status === 'pending' || code === 1) return { text: 'Pendiente', cssClass: 'warn' };
  return { text: 'Sin estado', cssClass: 'def' };
}

export function txHashFromExplorer(explorerUrl?: string | null, txHash?: string | null): string | undefined {
  if (txHash) return txHash;
  const match = explorerUrl?.match(/\/tx\/(0x[a-fA-F0-9]+)/i);
  return match?.[1];
}

interface OtherMemberRef {
  otherMemberId?: number | string | null;
  otherMemberCode?: number | string | null;
}

// El backend pasó de `otherMemberCode` a `otherMemberId`; se aceptan ambos.
export function otherMemberIdOf(collaboration: OtherMemberRef): number | string | undefined {
  const id = collaboration.otherMemberId ?? collaboration.otherMemberCode;
  return id === null || id === '' ? undefined : id;
}

export function isGhostMemberCollaboration(collaboration: OtherMemberRef): boolean {
  const id = otherMemberIdOf(collaboration);
  return id === 0 || id === '0';
}

export function categoryLabel(category?: string): string {
  const key = (category || '').toUpperCase();
  return CATEGORY_LABEL[key] || category || 'Sin categoría';
}

export function docKind(mimeType?: string): 'image' | 'pdf' | 'other' {
  if ((mimeType || '').startsWith('image/')) return 'image';
  if (mimeType === 'application/pdf') return 'pdf';
  return 'other';
}

export interface DocumentChangeCounts {
  added: number;
  removed: number;
}

function asCount(value: unknown): number | null {
  if (value == null || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function asListLength(value: unknown): number | null {
  return Array.isArray(value) ? value.length : null;
}

export function documentChangeCounts(data: any): DocumentChangeCounts {
  const added =
    asCount(data?.documentsAddedCount) ?? asListLength(data?.documentsAdded) ?? 0;
  const removed =
    asCount(data?.documentsRemovedCount) ?? asListLength(data?.documentsRemoved) ?? 0;
  return { added, removed };
}

function documentChangeLabel(counts: DocumentChangeCounts): string {
  const parts: string[] = [];
  if (counts.added) {
    parts.push(`${counts.added} documento${counts.added > 1 ? 's' : ''} subido${counts.added > 1 ? 's' : ''}`);
  }
  if (counts.removed) {
    parts.push(
      `${counts.removed} documento${counts.removed > 1 ? 's' : ''} eliminado${counts.removed > 1 ? 's' : ''}`,
    );
  }
  return parts.join(' · ');
}

export interface DetailTimelineRow {
  cssClass: string;
  iconKind: ActivityIconKind;
  date: string;
  time: string;
  dateTime: string;
  isoDate?: string;
  label: string;
  txHash?: string;
  explorerUrl?: string;
  documentsAdded?: any[];
  documentsRemoved?: any[];
}

export function buildCommitmentTimelineRows(events: CommitmentEvent[]): DetailTimelineRow[] {
  return [...events]
    .sort((a, b) => a.timestamp - b.timestamp)
    .map(event => {
      if (event.type === 'PersonCommitmentDocumentsUpdated') {
        const counts = documentChangeCounts(event.data);
        const added = counts.added > 0 && Array.isArray(event.data?.documentsAdded)
          ? event.data.documentsAdded
          : [];
        const removed = counts.removed > 0 && Array.isArray(event.data?.documentsRemoved)
          ? event.data.documentsRemoved
          : [];
        return {
          cssClass: 'update',
          iconKind: eventIconKind(event),
          date: fmtDate(event.date),
          time: fmtTime(event.date),
          dateTime: fmtDateTime(event.date),
          isoDate: event.date,
          label: documentChangeLabel(counts) || 'Se actualizaron los documentos',
          txHash: event.txHash,
          explorerUrl: event.explorerUrl,
          documentsAdded: added,
          documentsRemoved: removed,
        };
      }
      const meta = DETAIL_EVENT_LABELS[event.type] || { label: 'Actualización registrada', cssClass: 'update' };
      return {
        cssClass: meta.cssClass,
        iconKind: eventIconKind(event),
        date: fmtDate(event.date),
        time: fmtTime(event.date),
        dateTime: fmtDateTime(event.date),
        isoDate: event.date,
        label: meta.label,
        txHash: event.txHash,
        explorerUrl: event.explorerUrl,
      };
    });
}

export function buildFallbackTimelineRows(commitment: {
  adherenceDate: string;
  adherenceTxHash: string;
  adherenceExplorerUrl: string;
  removalDate?: string;
  removalTxHash?: string;
  removalExplorerUrl?: string;
}): DetailTimelineRow[] {
  const rows: DetailTimelineRow[] = [
    {
      cssClass: 'created',
      iconKind: 'commitment-added',
      date: fmtDate(commitment.adherenceDate),
      time: fmtTime(commitment.adherenceDate),
      dateTime: fmtDateTime(commitment.adherenceDate),
      isoDate: commitment.adherenceDate,
      label: 'Se adhirió al compromiso',
      txHash: commitment.adherenceTxHash,
      explorerUrl: commitment.adherenceExplorerUrl,
    },
  ];
  if (commitment.removalDate) {
    rows.push({
      cssClass: 'deleted',
      iconKind: 'commitment-removed',
      date: fmtDate(commitment.removalDate),
      time: fmtTime(commitment.removalDate),
      dateTime: fmtDateTime(commitment.removalDate),
      isoDate: commitment.removalDate,
      label: 'Se dio de baja del compromiso',
      txHash: commitment.removalTxHash,
      explorerUrl: commitment.removalExplorerUrl,
    });
  }
  return rows;
}

export interface CollaborationGroup {
  code: string;
  title: string;
  deleted: boolean;
  sent: boolean;
  otherCode: number | string;
  latest: HistoryEvent;
  last: HistoryEvent;
  displayData: any;
}

function mergeEventData(events: HistoryEvent[]): any {
  const merged: any = {};
  events.forEach(event => {
    Object.entries(event.data || {}).forEach(([key, value]) => {
      if (value !== undefined && value !== null) merged[key] = value;
    });
  });
  return merged;
}

export function isGhostCollaborationEvent(event: HistoryEvent): boolean {
  const data = event.data || {};
  return (
    isZeroAddress(data.sourceMemberAddress) ||
    isZeroAddress(data.targetMemberAddress) ||
    data.sourceMemberCode === 0 ||
    data.sourceMemberCode === '0'
  );
}

function isGhostCollaborationGroup(group: CollaborationGroup): boolean {
  const data = group.displayData || {};
  return (
    group.otherCode === 0 ||
    group.otherCode === '0' ||
    group.otherCode == null ||
    isZeroAddress(data.sourceMemberAddress) ||
    isZeroAddress(data.targetMemberAddress)
  );
}

function collaborationCode(event: HistoryEvent): string | number | null {
  if (event.type === 'CollaborationAddedComment') return event.data?.collaborationCode ?? null;
  return event.data?.code ?? event.data?.collaborationCode ?? null;
}

export function groupCollaborations(events: HistoryEvent[], walletAddress: string): CollaborationGroup[] {
  const groups: Record<string, HistoryEvent[]> = {};
  events
    .filter(e => e.type.startsWith('Collaboration'))
    .filter(e => !isGhostCollaborationEvent(e))
    .forEach(e => {
      const code = collaborationCode(e);
      if (code == null) return;
      (groups[code] = groups[code] || []).push(e);
    });

  return Object.entries(groups)
    .map(([code, evs]) => {
      const sorted = [...evs].sort((a, b) => a.timestamp - b.timestamp);
      const deleted = sorted.some(e => e.type === 'CollaborationDeleted');
      const withMembers = [...sorted].reverse().find(
        e => e.data?.sourceMemberAddress && !isZeroAddress(e.data.sourceMemberAddress),
      );
      const latest = withMembers || sorted[sorted.length - 1];
      const displayData = mergeEventData(sorted);
      const title =
        [...sorted].reverse().find(e => e.data?.title)?.data?.title ||
        displayData.title ||
        `Colaboración #${code}`;
      const sent = isSelf(displayData.sourceMemberAddress || latest.data?.sourceMemberAddress, walletAddress);
      const otherCode = sent
        ? displayData.targetMemberCode ?? latest.data?.targetMemberCode
        : displayData.sourceMemberCode ?? latest.data?.sourceMemberCode;
      const last = sorted[sorted.length - 1];
      return { code, title, deleted, sent, otherCode, latest, last, displayData };
    })
    .filter(group => !isGhostCollaborationGroup(group))
    .sort((a, b) => new Date(b.last.date).getTime() - new Date(a.last.date).getTime());
}

export function eventStatusChip(event: HistoryEvent): { text: string; cssClass: string } | null {
  if (event.type !== 'CollaborationModified') return null;
  const meta = collaborationStatusMeta(event.data);
  if (meta.text === 'Sin estado') return null;
  if (
    event.subType === 'status_update' ||
    event.subType === 'finalized' ||
    event.data?.status ||
    event.data?.statusCode != null
  ) {
    return meta;
  }
  return null;
}

// El evento puede venir sin código de miembro; evita "Miembro #undefined".
function memberRef(code: number | string | null | undefined): string {
  return code == null || code === '' ? 'un miembro' : `Miembro #${code}`;
}

export function eventLabel(
  event: HistoryEvent,
  walletAddress: string,
  collabTitles: Record<string, string>,
  commitmentTitles: Record<string, string>,
): string {
  const title = event.data?.title;
  switch (event.type) {
    case 'CommitmentCreated':
      return `Compromiso creado: ${title || '#' + event.data?.code}`;
    case 'CommitmentModified':
      if (event.subType === 'enabled') return `Compromiso ${title} habilitado`;
      if (event.subType === 'disabled') return `Compromiso ${title} deshabilitado`;
      return `Datos actualizados en ${title}`;
    case 'CommitmentDeleted':
      return 'Compromiso eliminado';
    case 'CollaborationCreated': {
      const sent = isSelf(event.data?.sourceMemberAddress, walletAddress);
      const cat = categoryLabel(event.data?.category);
      return sent
        ? `Colaboración ${cat} enviada a ${memberRef(event.data?.targetMemberCode)}`
        : `Colaboración ${cat} recibida de ${memberRef(event.data?.sourceMemberCode)}`;
    }
    case 'CollaborationModified': {
      const collabTitle =
        title || collabTitles[event.data?.code] || `colaboración #${event.data?.code}`;
      const chip = eventStatusChip(event);
      if (event.subType === 'finalized') return `Colaboración ${collabTitle} finalizada`;
      if (chip && event.subType === 'status_update') {
        return `Colaboración ${collabTitle} actualizada a`;
      }
      return `Colaboración ${collabTitle} actualizada`;
    }
    case 'CollaborationDeleted':
      return 'Colaboración eliminada';
    case 'PersonCommitmentDocumentsUpdated': {
      const counts = documentChangeCounts(event.data);
      const commitTitle =
        commitmentTitles[event.data?.commitmentCode] || `compromiso #${event.data?.commitmentCode}`;
      const change = documentChangeLabel(counts);
      return change ? `${change} en ${commitTitle}` : `Documentos actualizados en ${commitTitle}`;
    }
    case 'CollaborationAddedComment': {
      const sent = isSelf(event.data?.sourceMemberAddress, walletAddress);
      const collabTitle =
        collabTitles[event.data?.collaborationCode] || `colaboración #${event.data?.collaborationCode}`;
      return sent
        ? `Comentario agregado en ${collabTitle}`
        : `${memberRef(event.data?.sourceMemberCode)} comentó en ${collabTitle}`;
    }
    default:
      return event.type;
  }
}

export function eventDotClass(event: HistoryEvent): string {
  if (event.type.endsWith('Deleted')) return 'def';
  if (event.type === 'PersonCommitmentDocumentsUpdated') {
    const counts = documentChangeCounts(event.data);
    if (counts.removed && !counts.added) return 'def';
    if (counts.added && !counts.removed) return 'suc';
  }
  if (event.type === 'CommitmentCreated' || event.type === 'CollaborationCreated') return 'suc';
  if (event.type === 'CollaborationAddedComment') return 'sec';
  if (event.subType === 'disabled') return 'def';
  return 'info';
}

export interface ActivityFeed {
  shown: HistoryEvent[];
  hiddenCount: number;
  total: number;
}

export function buildActivityFeed(
  events: HistoryEvent[],
  walletAddress: string,
  limit = 15,
  offset = 0,
): ActivityFeed {
  const ownEvents = events.filter(e => {
    if (e.type === 'CollaborationAddedComment') return isSelf(e.data?.sourceMemberAddress, walletAddress);
    if (e.type.startsWith('Collaboration') && isGhostCollaborationEvent(e)) return false;
    return true;
  });
  const sorted = [...ownEvents].sort((a, b) => b.timestamp - a.timestamp);
  const start = Math.max(0, offset);
  return {
    shown: sorted.slice(start, start + limit),
    hiddenCount: Math.max(0, sorted.length - start - limit),
    total: sorted.length,
  };
}

export function collectTitleMaps(events: HistoryEvent[]): {
  collabTitles: Record<string, string>;
  commitmentTitles: Record<string, string>;
} {
  const collabTitles: Record<string, string> = {};
  const commitmentTitles: Record<string, string> = {};
  events.forEach(e => {
    if (
      e.data?.title &&
      e.data?.code != null &&
      e.type.startsWith('Collaboration') &&
      e.type !== 'CollaborationAddedComment'
    ) {
      collabTitles[e.data.code] = e.data.title;
    }
    if (e.data?.title && e.data?.code != null && e.type.startsWith('Commitment')) {
      commitmentTitles[e.data.code] = e.data.title;
    }
  });
  return { collabTitles, commitmentTitles };
}
